/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams, useLocation } from '@reach/router'

import { useToast } from '_/hooks/use-toast'
import useRolePermission from '_/hooks/use-role-permission'
import { useDialog } from '_/hooks/use-dialog'
import { getServiceOrder } from '_/modules/service-orders/actions'
import { Grid } from '@mui/material'
import { Typography, Menu, MenuItem } from '@material-ui/core'
import { Button, Datagrid, useConfirm, Loader } from '@refera/ui-web'

import HeaderTitle from '_/components/header-title'
import ConfirmRemoveDialog from '_/components/dialogs/ConfirmRemoveDialog'
import { SaveModelsModal } from './save-modal'
import { LoadModelsModal } from './load-modal'
import { ServiceModal } from './components/ServiceModal'

import useStyles from './styles'

import { TABLE_SX } from '../finance/utils/constants'
import { BudgetServicesColumns } from './constants'

import {
  fetchBudgetServices,
  fetchBudgetAdjustServices,
  postBudgetSaveTemplates,
  postBudgetService,
  postBudgetLoadTemplates,
  patchBudgetService,
  deleteBudgetService,
  deleteUnsavedBudgetServices,
} from '_/modules/budget/actions'

import { ROUTES } from '_/utils/constants'
import { NotFound } from '../error-screens'
import { verifyBudgetFromPath } from '_/utils/verify-budget'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

export function BudgetServices() {
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const queryParams = new URLSearchParams(search)
  const budgetId = queryParams.get('budgetId') || null
  const { checkUserPermission } = useRolePermission()
  const { budgetIsSaved } = verifyBudgetFromPath(pathname)
  const { showDialog, closeDialog } = useDialog()
  const { showToast } = useToast()
  const useNewBudget = queryParams.get('novoOrcamento') === 'true'
  const { serviceOrderId } = useParams()

  // Verifica se o usuário tem permissão para acessar serviços do orçamento
  const showScreen = useMemo(() => {
    try {
      const hasPermission = checkUserPermission({
        permission: PERMISSIONS.BUDGET_SERVICES,
        permissionAction: PERMISSIONS_ACTIONS.READ,
        functionAccess: () => true,
        silent: true,
      })
      return hasPermission === true
    } catch {
      return false
    }
  }, [checkUserPermission])

  const goBack = () => {
    if (useNewBudget) {
      navigate(`${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${budgetId}?edit=true`)
    } else {
      navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}/orcamento/${budgetId}`)
    }
  }

  if (!budgetIsSaved) {
    // Message 5
    showDialog({
      type: 'warning',
      subject: 'Atenção',
      description: 'Salve antes as edições deste registro.',
      onApprove: closeDialog,
      labelApprove: 'Ok, entendi',
    })
    goBack()
  }
  // TODO: budget is always undefined in this screen
  // else if (!budgetIsInternal) {
  //   // Message 591
  //   showToast({
  //     message: 'Esta ação só é aceita para editar orçamentos internos.',
  //     type: 'error',
  //   })
  //   goBack()
  // }

  if (!showScreen) {
    navigate('/')
    return null
  }

  if (!budgetId) {
    return <NotFound />
  }

  const dispatch = useDispatch()
  const styles = useStyles()

  const [isLoading, setIsLoading] = useState(false)
  const [dialogInfo, setDialogInfo] = useState()
  const [budgetServices, setBudgetServices] = useState([])
  const [isNewServiceAdded, setIsNewServiceAdded] = useState(false)
  const [serviceModalProps, setServiceModalProps] = useState({
    isOpen: false,
    loading: false,
    defaultValues: undefined,
    onSaveCallback: undefined,
  })
  const [pageLoading, setPageLoading] = useState(false)
  const [persistedFilters, setPersistedFilters] = useState({
    filterJustCategories: false,
    filterProblem: null,
    filterCategory: null,
  })

  const { isConfirmed } = useConfirm()
  const [saveModalOpen, handleSaveModal] = useState(false)
  const [loadModalOpen, handleLoadModal] = useState(false)
  const [pageLoadedDatetime, setPageLoadedDatetime] = useState(new Date())
  const [compartmentList, setCompartmentList] = useState([])
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)

  const totalPrice = budgetServices.reduce(
    (acc, service) => acc + parseFloat(service.totalPrice),
    0
  )

  /**
   * Always use this method instead of setBudgetServices.
   * @param args {{data?: any[], initial?: any[]}} */
  function handleSetBudgetServices(args) {
    setBudgetServices(prev => {
      const newInitial = args?.initial || prev.initial
      let newData = args?.data || prev.data
      const initialIds = newInitial.map(s => s.id)
      newData = newData.filter(service => !initialIds.includes(service.id))
      const result = { data: newData, initial: newInitial }
      return result
    })
  }

  const getBudgetServices = useCallback(() => {
    setIsLoading(true)

    const params = { budget: budgetId, created_at__gt: pageLoadedDatetime }

    dispatch(fetchBudgetServices(params))
      .then(data => {
        setBudgetServices(data)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
        showToast({ type: 'error', message: 'Ocorreu um erro ao buscar os registros.' })
      })
  }, [dispatch, pageLoadedDatetime])

  const createNewService = useCallback(
    async newServiceDTO => {
      setPageLoading(true)

      const {
        service: { id: serviceId },
        ...rest
      } = newServiceDTO

      const payload = {
        service: serviceId,
        ...rest,
        budget: budgetId ? parseInt(budgetId, 10) : null,
        service_order: parseInt(serviceOrderId, 10),
      }

      try {
        if (!isNewServiceAdded) {
          await dispatch(deleteUnsavedBudgetServices(budgetId))
          setIsNewServiceAdded(true)
        }
        dispatch(postBudgetService(payload))
          .then(() => {
            setPageLoading(false)
            setServiceModalProps(prev => ({ ...prev, isOpen: false }))
            getBudgetServices()
            showToast({ type: 'success' })
          })
          .catch(() => {
            setPageLoading(false)
            showToast({ type: 'error' })
          })
      } catch {
        setPageLoading(false)
        showToast({ type: 'error' })
      }
    },
    [budgetId, isNewServiceAdded]
  )

  const updateService = useCallback(
    (defaultValues, updatedServiceDTO) => {
      setPageLoading(true)

      const {
        service: { id: serviceId },
        ...rest
      } = updatedServiceDTO

      const payload = {
        service: serviceId,
        ...rest,
        budget: budgetId ? parseInt(budgetId, 10) : null,
        service_order: parseInt(serviceOrderId, 10),
      }

      dispatch(patchBudgetService(defaultValues.id, payload))
        .then(() => {
          setPageLoading(false)
          setServiceModalProps(prev => ({ ...prev, isOpen: false }))
          getBudgetServices()
          showToast({ type: 'success' })
        })
        .catch(() => {
          setPageLoading(false)
          showToast({ type: 'error' })
        })
    },
    [budgetId]
  )

  const handleBudgetAdjustServices = useCallback(async () => {
    const serviceOrder = await dispatch(getServiceOrder(serviceOrderId))
    const isVacancy = serviceOrder?.vacancy

    if (isVacancy) {
      const servicesWithoutCompartment = budgetServices.filter(service => !service.compartment)
      if (servicesWithoutCompartment.length > 0) {
        showToast({
          type: 'error',
          message:
            'Todos os registros devem ter o campo Cômodo preenchido para prosseguir com o ajuste de orçamento.',
        })
        return
      }
    }

    setDialogInfo({ message: 'Você tem certeza que deseja executar esta ação?' })
    const confirmed = await isConfirmed()
    if (!confirmed) return

    let confirmedRevenueShare = false

    if (!isVacancy) {
      setDialogInfo({ message: 'Você deseja calcular o "Revenue Share" no orçamento?' })
      confirmedRevenueShare = await isConfirmed()
    }

    const payload = {
      calculateRevenueShare: confirmedRevenueShare,
      created_at__gt: pageLoadedDatetime,
      useNewBudget,
    }
    setPageLoading(true)

    await dispatch(fetchBudgetAdjustServices(budgetId, payload))
      .then(() => {
        dispatch(getServiceOrder(serviceOrderId)).then(() => {
          setPageLoading(false)
          showToast({
            type: 'success',
          })
          goBack()
        })
      })
      .catch(error => {
        console.error('budget-adjust-services ->', error)
        setPageLoading(false)
        showToast({
          type: 'error',
          message: error?.error_message || 'Ocorreu um erro desconhecido ao executar a ação.',
        })
      })
  }, [dispatch, budgetServices, serviceOrderId])

  const handleMenuClick = useCallback(event => {
    setMenuAnchorEl(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null)
  }, [])

  const toggleSaveTemplatesModal = useCallback(() => {
    handleMenuClose()
    if (!budgetServices?.length) {
      showToast({
        type: 'error',
        message: 'Não há serviços selecionados para realizar esta ação.',
      })
      return
    }

    handleSaveModal(prevState => !prevState)
  }, [budgetServices, handleMenuClose])

  const handleSaveTemplates = useCallback(
    async payload => {
      if (!isNewServiceAdded) {
        await dispatch(deleteUnsavedBudgetServices(budgetId))
        setIsNewServiceAdded(true)
      }

      const servicesWithCompartments = budgetServices.map(service => ({
        ...service,
        compartment: service.compartment || null,
      }))

      const appendPayload = {
        created_at__gt: pageLoadedDatetime,
        services: servicesWithCompartments,
      }

      await dispatch(postBudgetSaveTemplates(budgetId, { ...payload, ...appendPayload }))
        .then(() => {
          setPageLoading(false)
          handleSaveModal(prevState => !prevState)
          showToast({
            type: 'success',
          })
        })
        .catch(error => {
          setPageLoading(false)
          showToast({
            type: 'error',
            message: error?.error_message || 'Ocorreu um erro desconhecido ao executar a ação.',
          })
        })
    },
    [budgetId, pageLoadedDatetime, isNewServiceAdded, budgetServices]
  )

  const toggleBudgetLoadTemplates = useCallback(async () => {
    handleMenuClose()
    setDialogInfo({ message: 'Você tem certeza que deseja executar esta ação?' })
    const confirmed = await isConfirmed()

    if (!confirmed) return
    handleLoadModal(prevState => !prevState)
  }, [dispatch, handleMenuClose])

  const handleBudgetLoadTemplates = useCallback(
    async payload => {
      handleLoadModal(prevState => !prevState)

      const serviceOrder = await dispatch(getServiceOrder(serviceOrderId))
      const isVacancy = serviceOrder?.vacancy

      const requestData = {
        templateId: payload?.templateId,
        created_at__gt: pageLoadedDatetime,
        excludeCompartments: !isVacancy,
      }

      if (payload?.saveValue) {
        // IF SAVE_VALUES IS TRUE, CHECKS FOR PRICE RECALCULATION
        setDialogInfo({ message: 'Você deseja recalcular os preços?' })
        const confirmed = await isConfirmed()
        requestData.recalculateValues = confirmed
      }

      try {
        if (!isNewServiceAdded) {
          await dispatch(deleteUnsavedBudgetServices(budgetId))
          setIsNewServiceAdded(true)
        }

        const data = await dispatch(postBudgetLoadTemplates(budgetId, requestData))

        const processedData = !isVacancy
          ? data.map(service => ({ ...service, compartment: null }))
          : data

        setBudgetServices(prev => [...prev, ...processedData])
        showToast({
          type: 'success',
        })
      } catch (error) {
        showToast({
          type: 'error',
          message: error?.error_message || 'Ocorreu um erro desconhecido ao executar a ação.',
        })
      }
    },
    [
      dispatch,
      handleLoadModal,
      budgetId,
      isConfirmed,
      setDialogInfo,
      handleSetBudgetServices,
      showToast,
      pageLoadedDatetime,
      isNewServiceAdded,
      serviceOrderId,
    ]
  )

  const handleCellClick = useCallback(({ field, row }) => {
    if (field === 'actions') return
    setServiceModalProps({
      isOpen: true,
      defaultValues: { ...row },
      onSaveCallback: updateService,
    })
  }, [])

  const handleDeleteServiceClick = useCallback(
    (event, row) => {
      event.stopPropagation()
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja excluir este serviço?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          setPageLoading(true)

          dispatch(deleteBudgetService(row.id))
            .then(() => {
              setPageLoading(false)

              getBudgetServices()
              closeDialog()
              showToast({ type: 'success' })
            })
            .catch(() => {
              setPageLoading(false)

              closeDialog()
              showToast({ type: 'error' })
            })
        },
        onCancel: closeDialog,
      })
    },
    [dispatch]
  )

  // On page first load
  useEffect(() => {
    setPageLoadedDatetime(new Date())
    if (budgetId) {
      getBudgetServices()
    }
  }, [])

  return (
    <Grid className={styles.page}>
      {dialogInfo?.message && <ConfirmRemoveDialog message={dialogInfo.message} />}
      <HeaderTitle title="Serviços do orçamento" backButtonAction={goBack}>
        <Grid className={styles.buttonsContainer}>
          <Button variant="secondary" onClick={handleMenuClick} disabled={isLoading || !budgetId}>
            Mais ações
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              style: {
                marginTop: 52,
              },
            }}
          >
            <MenuItem disabled={isLoading || !budgetId} onClick={toggleBudgetLoadTemplates}>
              Carregar modelo
            </MenuItem>
            <MenuItem disabled={isLoading || !budgetId} onClick={toggleSaveTemplatesModal}>
              Salvar modelo
            </MenuItem>
          </Menu>
          <Button
            variant="secondary"
            onClick={handleBudgetAdjustServices}
            disabled={isLoading || !budgetId}
          >
            Ajustar orçamento
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setServiceModalProps({
                isOpen: true,
                defaultValues: undefined,
                onSaveCallback: createNewService,
              })
            }}
            disabled={isLoading}
          >
            Novo
          </Button>
        </Grid>
      </HeaderTitle>
      <Grid className={styles.tableContainer}>
        <Grid className={styles.totalValueContainer}>
          <Typography>
            <b>Total: </b>
            {totalPrice.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
          </Typography>
        </Grid>
        <Grid className={styles.mainTable}>
          <Datagrid
            initialState={{
              sorting: {
                sortModel: [
                  { field: 'compartment', sort: 'asc' },
                  { field: 'serviceName', sort: 'asc' },
                ],
              },
            }}
            loading={isLoading}
            rows={budgetServices}
            columns={BudgetServicesColumns(handleDeleteServiceClick)}
            paginationMode="client"
            rowCount={budgetServices?.length || 0}
            sx={TABLE_SX}
            noRowsLabel="Nenhum resultado encontrado"
            onCellClick={handleCellClick}
          />
        </Grid>
      </Grid>

      <SaveModelsModal
        open={saveModalOpen}
        handleConfirm={handleSaveTemplates}
        handleModal={() => toggleSaveTemplatesModal()}
      />

      <LoadModelsModal
        open={loadModalOpen}
        handleConfirm={handleBudgetLoadTemplates}
        handleModal={() => handleLoadModal(prevState => !prevState)}
      />

      <ServiceModal
        budgetServices={budgetServices}
        clearOnClose={false}
        defaultValues={serviceModalProps.defaultValues}
        loading={pageLoading}
        onClose={() => setServiceModalProps(prev => ({ ...prev, isOpen: false }))}
        open={serviceModalProps.isOpen}
        onSaveButtonClick={serviceModalProps.onSaveCallback}
        compartmentList={compartmentList}
        setCompartmentList={setCompartmentList}
        persistedFilters={persistedFilters}
        onFiltersChange={setPersistedFilters}
      />
      <Loader open={pageLoading} hasBackdrop />
    </Grid>
  )
}
